'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@eliadmin/core/src/components/ui/select';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { changeLanguage } from '@eliadmin/core/src/server/api-client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getFlagEmoji } from '@eliadmin/core/src/utils/locale-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useEffect, useState } from 'react';

export function LanguageSelect() {
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocaleStore(s => s.currentLocale);
  const locales = useLocaleStore(s => s.supportedLocales);
  const [selectedLocale, setSelectedLocale] = useState(
    locales.find(localeIterator => localeIterator.locale === locale),
  );

  const handleLocaleChange = async (newLocale: string) => {
    const newRoute = `/${newLocale}/`;
    setIsLoading(true);
    await changeLanguage({ locale: newLocale });
    window.location.replace(newRoute);
  };

  useEffect(() => {
    setSelectedLocale(locales.find(localeIterator => localeIterator.locale === locale));
  }, [locale]);

  return (
    <div className={cn('w-auto', 'ea-language-select')}>
      <Select value={locale} onValueChange={handleLocaleChange}>
        <SelectTrigger
          aria-label={selectedLocale?.name?.trim() || 'Language'}
          className={cn('bg-transparent', 'ea-language-select__trigger')}
          disabled={isLoading}
          innerTriggerClassName="h-auto"
        >
          {isLoading ? (
            <Spinner className={cn('w-8', 'ea-language-select__spinner')} />
          ) : selectedLocale ? (
            <div className="flex items-center justify-center gap-3">
              <Image
                src={selectedLocale?.image?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(selectedLocale?.image, selectedLocale?.locale)}
                width={20}
                height={20}
              />
              <p className="mr-3 text-xl uppercase text-white">{selectedLocale.locale}</p>
            </div>
          ) : (
            <span className={cn('px-2 text-xl', 'ea-language-select__flag')}>
              {getFlagEmoji(locale)}
            </span>
          )}
        </SelectTrigger>
        <SelectContent
          className={cn(
            'w-full max-w-[140px] border-none bg-[#AC1616]',
            'ea-language-select__content',
          )}
        >
          {locales.map(locale => (
            <SelectItem
              key={`${locale.name}-${locale.locale}`}
              value={locale.locale}
              className={cn('!bg-transparent', 'ea-language-select__item')}
            >
              <div className="flex items-center justify-center gap-3">
                <Image
                  src={locale?.image?.url ?? '/images/placeholder.png'}
                  alt={resolveImageAlt(locale?.image, locale?.locale)}
                  width={20}
                  height={20}
                />
                <p className="mr-3 text-xl uppercase text-white">{locale.locale}</p>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
